import { useEffect, useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'

import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgFillLinkCircleLM from '@jotta/ui/SvgFillLinkCircleLM'
import { cn } from '@jotta/utils/css'

import type { PathItemStore } from './PathItemStore'

import styles from './PathItemRow.module.scss'
const debug = Debug('jotta:files:PathItemThumb')

export interface PathItemThumbProps {
  item: PathItemStore
}

export const PathItemThumb = observer<PathItemThumbProps>(
  function PathItemThumb({ item }: PathItemThumbProps) {
    const [error, setError] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const { thumbLink, fileKindConfig, isFile, isShared } = item

    useEffect(() => {
      setLoaded(false)
      setError(false)
    }, [thumbLink])

    const hasThumb = Boolean(thumbLink)
    const showLoadingPlaceholder = hasThumb && !error && !loaded
    const showFallbackIcon = !hasThumb || error

    return (
      <span className={styles.icon}>
        {hasThumb && !error && (
          <img
            key={thumbLink}
            data-testid="hasThumb"
            src={thumbLink}
            onLoad={() => {
              setLoaded(true)
            }}
            onError={() => {
              setError(true)
            }}
            className={cn(
              'overflow-hidden rounded-md object-cover transition-opacity duration-200',
              loaded ? 'opacity-100' : 'absolute opacity-0',
            )}
            loading={loaded ? 'lazy' : 'eager'}
            decoding="async"
            alt=""
          />
        )}

        {showLoadingPlaceholder && (
          <span
            data-testid="showPlaceholder"
            className={cn(styles.svgicon, {
              'rounded-md bg-file-element p-1': isFile,
            })}
            aria-hidden
          >
            <PlainBrandIcon
              icon={fileKindConfig.icon}
              height={isFile ? 24 : 32}
              aria-label={fileKindConfig.label}
            />
          </span>
        )}

        {showFallbackIcon && (
          <span
            data-testid="fallbackIcon"
            className={cn(styles.svgicon, {
              'rounded-md bg-file-element p-1': isFile,
            })}
            aria-hidden
          >
            <PlainBrandIcon
              icon={fileKindConfig.icon}
              height={isFile ? 24 : 32}
              aria-label={fileKindConfig.label}
            />
          </span>
        )}
        {isShared && (
          <PlainBrandIcon
            icon={SvgFillLinkCircleLM}
            className={styles.shareicon}
          />
        )}
      </span>
    )
  },
)
